import React, { HTMLAttributes } from "react";
import { useIntl, useLocale } from "@/i18n/i18n-client";
import styled from "styled-components";
import { DownArrow } from "@/assets/Icons/DownArrow";

interface SelectWithIconProps<OptionType>
  extends Omit<HTMLAttributes<HTMLSelectElement>, "onChange"> {
  className?: string;
  dataTest?: string;
  options: OptionType[];
  placeholder?: string;
  keyField?: string;
  disabled?: boolean;
  value: OptionType;
  onChange: (selected: OptionType) => void;
  renderOption: (value: OptionType) => string;
}

function SelectWithIcon<OptionType = any>(
  props: SelectWithIconProps<OptionType>
) {
  const {
    className,
    dataTest,
    options,
    renderOption,
    keyField = "value",
    disabled,
    value,
    onChange,
    placeholder = "--Select--",
    ...restProps
  } = props;
  const intl = useIntl();
  const { dir } = useLocale();
  const isRtl = dir === "rtl";
  const valueIndex = options?.findIndex(
    (option) => option?.[keyField] === value?.[keyField]
  );

  const handleChange = ({ target: { value: index } }) => {
    onChange(options[index]);
  };

  const renderOptions = options?.map((option, index) => (
    <option data-test="select-option" key={index} value={index}>
      {renderOption(option)}
    </option>
  ));

  return (
    <div className="relative">
      <Select
        {...restProps}
        data-test={dataTest}
        className={`${className} field-input`}
        disabled={disabled}
        onChange={handleChange}
        value={valueIndex === -1 ? "" : valueIndex}
      >
        <option value="" hidden>
          {placeholder}
        </option>
        {!options?.length && (
          <option
            label={intl.formatMessage({
              defaultMessage: "No Results",
              id: "k8bltk",
            })}
            disabled
          />
        )}
        {renderOptions}
      </Select>
      <span
        className={`absolute top-1/2 -translate-y-1/2 ${isRtl ? "left-[10px]" : "right-[10px]"} !text-gray-500`}
      >
        <DownArrow />
      </span>
    </div>
  );
}

export default SelectWithIcon;

/**
 * Styles
 */

const Select = styled.select`
  width: 100%;
  -webkit-appearance: none;
`;
